import { map, once } from "ramda";
import { Maybe } from "true-myth";
import { addDays, differenceInCalendarDays, format, getYear } from "date-fns";

function dateParamToDate(date: Maybe<string>): Maybe<Date> {
  return date.map((dateParam: string) => {
    const [year, month, day]: number[] = map(
      (datePart: string) => parseInt(datePart, 10),
      dateParam.split("-")
    );
    // Months start counting at 0
    return new Date(year, month - 1, day);
  });
}

enum DateFormats {
  DATETIME = "MMM DD, YYYY HH:mm",
  MONTH_DAY = "MMM DD",
  MONTH_DAY_YEAR = "MM/DD/YY",
  MONTH_DAY_YEAR_LONG = "MMM DD, YYYY",
  MONTH_YEAR = "MMM YYYY",
  TIME = "h:mm a",
  TIME_COMPRESSED = "h:mma",
  WEEKDAY = "dddd",
  WEEKDAY_DATE = "ddd, MMM D",
  WEEKDAY_FULL = "dddd, MMM DD",
  WEEKDAY_ORD = "ddd, MMM Do",
  YEAR_MONTH_DAY = "YYYY-MM-DD",
}

function dateToDateParam(date: Maybe<Date>): Maybe<string> {
  return date.map((dateObj: Date) => {
    return format(dateObj, DateFormats.YEAR_MONTH_DAY);
  });
}

export type Weekday =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";
function dateToWeekday(date: Date): Weekday {
  return format(date, DateFormats.WEEKDAY) as Weekday;
}

// Add 7 and mod because we need to loop around when thisWeekday < scheduleStartsOn
// eg: startWeekDay Wednesay = 3, endWeekDay Monday = 1 (1 - 3 + 7) % 7 = 5 days ahead
function differenceInWeekdays(startWeekday: number, endWeekDay: number) {
  return (endWeekDay - startWeekday + 7) % 7;
}

const getLastYear = once(() => getYear(new Date()) - 1);

// Keep the hour and minutes but reassign the date
function moveDate(date: Date, moveToDate: Date): Date {
  const daysDifference = differenceInCalendarDays(moveToDate, date);
  return addDays(date, daysDifference);
}

// Returns the end of the week given a start date.
// Differs from date-fns `endOfWeek` because it doesn't require
// `weekStartsOn` to align to proper weekday.
function sixDaysLater(start: Date): Date {
  return addDays(start, 6);
}

export {
  DateFormats,
  dateParamToDate,
  dateToDateParam,
  dateToWeekday,
  differenceInWeekdays,
  getLastYear,
  moveDate,
  sixDaysLater,
};
